import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface HeaderProps {
  className?: string;
}

const Header: React.FC<HeaderProps> = ({ className = "" }) => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [currentLang, setCurrentLang] = useState<string>(i18n.language);
  
  const toggleMenu = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const changeLanguage = (lang: string): void => {
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
  };

  return (
    <header className={`bg-white shadow-md py-4 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="mypa logo" 
              className="h-10 w-auto"
            />
            <span className="ml-2 text-xl font-bold text-mypa-blue">mypa</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.home")}
            </Link>
            <Link to="/notre-approche" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.approach")}
            </Link>
            <Link to="/nos-offres" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.offers")}
            </Link>
            <Link to="/segments-clients" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.segments")}
            </Link>
            <Link to="/calculateur-carbone" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.calculator")}
            </Link>
            <Link to="/blog" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.blog")}
            </Link>
            <Link to="/a-propos" className="text-gray-700 hover:text-mypa-green transition-colors">
              {t("nav.about")}
            </Link>
            <Link to="/contact" className="bg-mypa-green text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition-colors">
              {t("nav.contact")}
            </Link>
            
            {/* Language Switcher */}
            <div className="relative ml-4">
              <button 
                className="flex items-center text-gray-700 hover:text-mypa-green transition-colors"
                onClick={() => changeLanguage(currentLang === "fr" ? "en" : "fr")}
              >
                {currentLang === "fr" ? "FR" : "EN"}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-gray-700 focus:outline-none"
            onClick={toggleMenu}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
              )}
            </svg>
          </button>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 py-2 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.home")}
              </Link>
              <Link 
                to="/notre-approche" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.approach")}
              </Link>
              <Link 
                to="/nos-offres" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.offers")}
              </Link>
              <Link 
                to="/segments-clients" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.segments")}
              </Link>
              <Link 
                to="/calculateur-carbone" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.calculator")}
              </Link>
              <Link 
                to="/blog" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.blog")}
              </Link>
              <Link 
                to="/a-propos" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.about")}
              </Link>
              <Link 
                to="/contact" 
                className="text-gray-700 hover:text-mypa-green transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("nav.contact")}
              </Link>
              
              {/* Language Switcher */}
              <div className="py-2 border-t border-gray-200 mt-2">
                <button 
                  className="flex items-center text-gray-700 hover:text-mypa-green transition-colors"
                  onClick={() => {
                    changeLanguage(currentLang === "fr" ? "en" : "fr");
                    setIsMenuOpen(false);
                  }}
                >
                  {currentLang === "fr" ? "Switch to English" : "Passer en français"}
                </button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
